import { useEffect, useState } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

/**
 * Reusable "Pause audit" modal — calls
 * GET /admin/publisher-brands/{publisher_id}/{brand_id}/pause-audit
 * and renders the diagnosis + outcome breakdown so admins can confirm
 * at a glance whether a paused brand's tracker gate is actually
 * blocking incoming traffic, or whether clicks are slipping past.
 *
 * Drop in anywhere we have a (publisher_id, brand_id, brand_name) tuple:
 *
 *   <PauseAuditModal
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     publisherId={pid}
 *     brandId={bid}
 *     brandName="Musician's Friend"
 *   />
 */
export default function PauseAuditModal({
  open,
  onClose,
  publisherId,
  brandId,
  brandName,
  defaultDays = 7,
}) {
  const [days, setDays] = useState(defaultDays);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !publisherId || !brandId) return;
    let cancelled = false;
    setLoading(true);
    API.get(`/admin/publisher-brands/${publisherId}/${brandId}/pause-audit`, {
      params: { days },
    })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Audit failed");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, publisherId, brandId, days]);

  const verdict = data?.diagnosis?.split(" — ")[0] || "";
  const isOK = verdict === "OK";
  const isUnknown = verdict === "UNKNOWN";
  const isInfo = verdict === "INFO";
  const isBug = verdict === "BUG";
  const banner = isOK
    ? { bg: "bg-emerald-50 border-emerald-200", txt: "text-emerald-700", title: "✓ Tracker blocking correctly" }
    : isUnknown
    ? { bg: "bg-slate-50 border-slate-300",     txt: "text-slate-700",   title: "? Insufficient data" }
    : isInfo
    ? { bg: "bg-blue-50 border-blue-200",       txt: "text-blue-700",    title: "ⓘ Assignment is active" }
    : { bg: "bg-red-50 border-red-200",         txt: "text-red-700",     title: "⚠ Bug — clicks landed despite pause" };
  const landed = data?.clicks_that_landed ?? null;
  const outcomes = data?.tracker_attempts_by_outcome || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="pause-audit-modal">
        <DialogHeader>
          <DialogTitle className="text-base">
            Pause audit — {brandName || "brand"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Confirms whether the tracker is actually blocking traffic to a paused brand.
            Looks at <span className="font-mono">tracker_hits</span> + <span className="font-mono">click_log</span> rows in the window.
          </DialogDescription>
        </DialogHeader>

        <div className="px-1 py-3 space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Window (days)</label>
            <div className="flex items-center gap-1">
              {[1, 7, 30].map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant={days === d ? "default" : "outline"}
                  className="h-7 px-2 text-xs"
                  onClick={() => setDays(d)}
                  data-testid={`pause-audit-window-${d}`}
                >
                  {d}d
                </Button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
          ) : data ? (
            <>
              <div
                className={`p-3 rounded-md border ${banner.bg}`}
                data-testid="pause-audit-diagnosis"
              >
                <div className={`text-xs font-bold uppercase tracking-wider ${banner.txt}`}>
                  {banner.title}
                </div>
                <div className="text-sm text-slate-700 mt-1.5 leading-snug">{data.diagnosis}</div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="border border-slate-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Current status</div>
                  <div className="font-mono">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      data.current_status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                    }`}>{data.current_status}</span>
                  </div>
                  {data.status_changed_at ? (
                    <div className="text-[10px] text-slate-400 mt-1.5 font-mono leading-snug">
                      paused since {data.status_changed_at.slice(0, 19).replace("T", " ")} UTC
                    </div>
                  ) : data.current_status === "paused" ? (
                    <div className="text-[10px] text-amber-600 mt-1.5 leading-snug">
                      Legacy row — no pause timestamp. Toggle Activate → Pause to re-stamp.
                    </div>
                  ) : null}
                </div>
                <div className="border border-slate-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Clicks landed since pause</div>
                  <div className={`font-mono text-lg font-bold ${landed === 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {landed?.toLocaleString() ?? "—"}
                  </div>
                  {data.effective_window_start && data.effective_window_start !== data.window_start ? (
                    <div className="text-[10px] text-slate-400 mt-1.5 leading-snug">
                      counted from {data.effective_window_start.slice(0, 19).replace("T", " ")} UTC
                    </div>
                  ) : (
                    <div className="text-[10px] text-slate-400 mt-1.5 leading-snug">
                      window: last {data.window_days}d
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
                  Tracker hits by outcome
                </div>
                {outcomes.length === 0 ? (
                  <div className="text-sm text-slate-400 italic">No hits in window.</div>
                ) : (
                  <div className="space-y-1">
                    {outcomes.map((o) => (
                      <div
                        key={o.outcome}
                        className="flex items-center justify-between text-sm border border-slate-200 rounded px-3 py-1.5"
                        data-testid={`pause-audit-outcome-${o.outcome}`}
                      >
                        <span className="font-mono text-slate-700">{o.outcome}</span>
                        <span className="font-mono font-bold">{o.count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {data.latest_landed_samples?.length > 0 && (
                <div>
                  <div className={`text-[10px] uppercase tracking-wider font-bold mb-1.5 ${isBug ? "text-red-700" : "text-slate-500"}`}>
                    {isBug
                      ? "Latest clicks that BYPASSED the gate (most recent first)"
                      : "Latest clicks in window (newest first)"}
                  </div>
                  <div className="space-y-1">
                    {data.latest_landed_samples.map((s, i) => (
                      <div key={i} className={`text-xs font-mono border rounded px-2 py-1 ${isBug ? "border-red-200 bg-red-50" : "border-slate-200 bg-slate-50"}`}>
                        {s.click_date} · rid={s.rid}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        <DialogFooter>
          <Button size="sm" variant="outline" onClick={onClose} data-testid="pause-audit-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
